/**
 * T070: Prompt Form Component
 * Main form for creating/editing prompts with react-hook-form and Zod validation
 */

import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CategorySelect } from './category-select'
import { TagMultiSelect } from './tag-multi-select'
import { PromptContentEditor } from './prompt-content-editor'
import type { PromptContent, PromptConfig, PromptMetadata } from '@/services/prompts'

// Zod schema for form validation
const textContentSchema = z.object({
  type: z.literal('text'),
  text: z.string().min(1, 'Content is required'),
})

const chatContentSchema = z.object({
  type: z.literal('chat'),
  messages: z.array(z.object({
    role: z.enum(['system', 'user', 'assistant']),
    content: z.string(),
  })).min(1, 'At least one message is required'),
})

const promptContentSchema = z.discriminatedUnion('type', [textContentSchema, chatContentSchema])

const promptFormSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  content: promptContentSchema,
  category_id: z.string().nullable(),
  tag_ids: z.array(z.string()),
  config: z.record(z.unknown()).nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
})

type PromptFormValues = z.infer<typeof promptFormSchema>

export interface PromptFormSubmitValues {
  title: string
  content: PromptContent
  category_id: string | null
  tag_ids: string[]
  config?: PromptConfig | null
  metadata?: PromptMetadata | null
}

interface PromptFormProps {
  defaultValues?: {
    title?: string
    content?: PromptContent
    category_id?: string | null
    tag_ids?: string[]
    config?: PromptConfig | null
    metadata?: PromptMetadata | null
  }
  onSubmit: (values: PromptFormSubmitValues) => void
  isSubmitting?: boolean
  submitLabel?: string
}

function getDefaultContent(content?: PromptContent): PromptFormValues['content'] {
  if (!content) {
    return { type: 'text', text: '' }
  }
  if (content.type === 'text') {
    return { type: 'text', text: content.text }
  }
  if (content.type === 'chat') {
    return { type: 'chat', messages: content.messages }
  }
  // Fallback for multipart or unknown types
  return { type: 'text', text: '' }
}

export function PromptForm({
  defaultValues,
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Save',
}: PromptFormProps) {
  const form = useForm<PromptFormValues>({
    resolver: zodResolver(promptFormSchema),
    defaultValues: {
      title: defaultValues?.title ?? '',
      content: getDefaultContent(defaultValues?.content),
      category_id: defaultValues?.category_id ?? null,
      tag_ids: defaultValues?.tag_ids ?? [],
      config: defaultValues?.config ?? null,
      metadata: defaultValues?.metadata ?? null,
    },
  })

  const handleSubmit = form.handleSubmit((values) => {
    // Convert form values to PromptContent
    const content: PromptContent = values.content.type === 'text'
      ? { type: 'text', text: values.content.text }
      : { type: 'chat', messages: values.content.messages }

    onSubmit({
      title: values.title,
      content,
      category_id: values.category_id,
      tag_ids: values.tag_ids,
      config: values.config,
      metadata: values.metadata,
    })
  })

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              {...form.register('title')}
              placeholder="Enter prompt title"
              disabled={isSubmitting}
            />
            {form.formState.errors.title && (
              <p className="text-sm text-destructive">{form.formState.errors.title.message}</p>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Category</Label>
              <Controller
                name="category_id"
                control={form.control}
                render={({ field }) => (
                  <CategorySelect
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <Controller
                name="tag_ids"
                control={form.control}
                render={({ field }) => (
                  <TagMultiSelect
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Content</CardTitle>
        </CardHeader>
        <CardContent>
          <Controller
            name="content"
            control={form.control}
            render={({ field }) => (
              <PromptContentEditor
                value={field.value as PromptContent}
                onChange={field.onChange}
                disabled={isSubmitting}
              />
            )}
          />
          {form.formState.errors.content && (
            <p className="mt-2 text-sm text-destructive">
              {form.formState.errors.content.message}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-4">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : submitLabel}
        </Button>
      </div>
    </form>
  )
}
